/**
 * Pure SSE + answer-parsing helpers for the chat widget (no React/DOM), so the
 * risky stream logic is unit-testable in Node against real /api/ask bytes.
 */

/**
 * Incremental SSE frame parser. Feed decoded text chunks; get back an array of
 * {event, data} for every complete frame seen so far. Holds a partial trailing
 * frame across calls. `data` is JSON-parsed (frames with unparseable data are skipped).
 */
export class SSEParser {
  constructor() { this._buf = ''; }

  feed(text) {
    this._buf += text;
    const frames = [];
    let idx;
    while ((idx = this._buf.indexOf('\n\n')) >= 0) {
      const raw = this._buf.slice(0, idx);
      this._buf = this._buf.slice(idx + 2);
      let event = null;
      let dataStr = '';
      for (const line of raw.split('\n')) {
        if (line.startsWith('event: ')) event = line.slice(7);
        else if (line.startsWith('data: ')) dataStr += line.slice(6);
      }
      if (!dataStr) continue;
      try { frames.push({ event, data: JSON.parse(dataStr) }); } catch { /* skip */ }
    }
    return frames;
  }
}

/**
 * Fold SSE frames into answer state — the same reducer useAsk applies, extracted
 * so it can be tested without React.
 */
export function reduceFrames(frames, state = { answer: '', sources: [], itinerary: null, used: [], done: null,
                                                error: null }) {
  for (const { event, data } of frames) {
    if (event === 'sources') state.sources = data;
    else if (event === 'itinerary') state.itinerary = data;
    else if (event === 'token') state.answer += (data.t || '');
    else if (event === 'replace') state.answer = data.t || '';
    else if (event === 'citations') state.used = data.used || [];
    else if (event === 'done') state.done = data;
    else if (event === 'error') state.error = data.message || 'error';
  }
  return state;
}

/**
 * Split answer text into ordered segments for rendering:
 *   {type:'text', value} | {type:'cite', n} | {type:'link', text, n}
 * 'link' is a page or form named in the answer, "[Change of Address Form][4]"
 * (member-services answers): the widget renders the name as a link to source
 * n. 'cite' is a bare "[4]" marker, rendered as a chip. Either renders only
 * when a source with that n exists. A marker repeating the link just before it
 * ("[Form][4] [4]") is dropped as redundant.
 */
export function splitAnswerSegments(text) {
  const segs = [];
  if (!text) return segs;
  const re = /\[(?!\d+\])([^[\]\n]{1,120})\]\[(\d+)\]|\[(\d+)\]/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    const between = text.slice(last, m.index);
    if (m[1] !== undefined) {
      if (between) segs.push({ type: 'text', value: between });
      segs.push({ type: 'link', text: m[1], n: parseInt(m[2], 10) });
    } else {
      const n = parseInt(m[3], 10);
      const prev = segs[segs.length - 1];
      const repeatsLink = prev && prev.type === 'link' && prev.n === n && !between.trim();
      if (!repeatsLink) {
        if (between) segs.push({ type: 'text', value: between });
        segs.push({ type: 'cite', n });
      }
    }
    last = re.lastIndex;
  }
  if (last < text.length) segs.push({ type: 'text', value: text.slice(last) });
  return segs;
}

/**
 * While an answer streams, a named link arrives in pieces: "[Change of Addr",
 * "[Change of Address Form]", "[Change of Address Form][1". Show the name
 * without brackets until the link completes, and hold back a half-typed
 * citation marker, so the bubble never flashes raw syntax.
 */
export function trimPartialLink(text) {
  if (!text) return text;
  return text
    .replace(/\[(?!\d)([^[\]\n]{0,120})(?:\](?:\[\d*)?)?$/, '$1')
    .replace(/\[\d*$/, '');
}

/**
 * Split plain answer text around email addresses and phone numbers so the
 * widget can make them tappable (mailto:/tel:). Answers only ever contain
 * addresses the server let through from the agency's own sources or its
 * configured closing line.
 *   {type:'text', value} | {type:'email', value} | {type:'phone', value, tel}
 */
export function splitContacts(text) {
  const segs = [];
  if (!text) return segs;
  const re = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+|(?:\+?1[-.\s]?)?(?:\(\d{3}\)\s?|\b\d{3}[-.\s])\d{3}[-.\s]\d{4}\b/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    let value = m[0];
    const isEmail = value.includes('@');
    if (isEmail) value = value.replace(/\.+$/, ''); // sentence-ending period
    if (m.index > last) segs.push({ type: 'text', value: text.slice(last, m.index) });
    if (isEmail) segs.push({ type: 'email', value });
    else segs.push({ type: 'phone', value, tel: value.replace(/[^\d+]/g, '') });
    last = m.index + value.length;
    re.lastIndex = last;
  }
  if (last < text.length) segs.push({ type: 'text', value: text.slice(last) });
  return segs;
}

/** Answer text for the clipboard: link names kept, citation markers dropped. */
export function plainAnswer(text) {
  return (text || '')
    .replace(/\[(?!\d+\])([^[\]\n]{1,120})\]\[\d+\]/g, '$1')
    .replace(/\s?\[\d+\]/g, '');
}
